import { seedMonthlyRent, seedMinimum, unitFees } from './pricingFacts.js'
import { depositFor } from '../property/settings.js'
import { depositFor as depositFromPolicy, policyForUnit } from '../property/depositPolicy.js'

/**
 * One home, in the shape the pricing engine prices — from the home and its Property setup.
 *
 * ONE PLACE, TWO READERS. The quote builder and the Listing page ask the same question: what
 * does this home cost for these months. If each built this object itself, a renter could see
 * one total on the website and another on the quote we send her for the same dates. So both
 * call this, and a test holds them to the same figure.
 *
 * `settings` is the home's Property setup, or null for a home nobody has been through.
 * `wholeMonths` picks the deposit band.
 * `policies` is every deposit policy. See the deposit block below for why it comes first.
 */
export function quoteUnitOf(u, settings, { wholeMonths, policies = [] }) {
  const cfg = settings ?? null
  /*
    A SAVED SETUP IS THE ONLY SOURCE FOR ITS HOME. A home with a Property setup row is priced from
    that row: a rent or floor it leaves empty stays empty, and the engine refuses the quote (NO_RENT)
    rather than filling it from Guesty's nightly rate — a figure nobody agreed. Only a home with no
    setup row at all is priced from the Guesty seed, as before. Found by review on #50: the Listing
    page said "no rent" while a quote for the same home went out on a derived one.
  */
  const monthly = cfg ? cfg.monthlyRent ?? null : seedMonthlyRent(u)
  /*
    THE FLOOR PROPERTY SETUP SAVED IS `floor`. This read `minimumRent`, a name nothing sets — carried
    over from the quote builder, where it had the same defect — so every quote fell back to the
    Guesty-derived seed: the agreed floor was never the one checked, and a home with an agreed rent
    but no Guesty price was refused as having no rent. Found by review on #50.
  */
  const minimum = cfg ? cfg.floor ?? null : seedMinimum(u)
  /*
    THE DEPOSIT POLICY DECIDES, AND UNTIL NOW IT DECIDED NOTHING. (mtr#115.)

    `policyForUnit` has existed since the policy screen was built and nothing outside that screen
    called it, so the Standard policy assigned to forty-two homes on 2026-09-28 changed no quote.
    Measured on production the same day: `deposit_policies` held one row with forty-two homes on
    it, and `property_settings` held forty-three rows with ZERO deposit figures — so every quote
    fell through to the engine's P-07 default of one month's rent, for every home. The screen
    showed one rule and the quote charged another, and nothing said so.

    Third costume of the same defect: the deposit screen that shipped without an import (mtr#95),
    the pricing strategies nothing ever called (mtr#103), and this. Each was correct, visible, and
    not connected to the place it was built for.

    THE POLICY BEATS THE HOME'S OWN LADDER, which is the Product Owner's decision and not an
    obvious one. The alternative is the reverse — a figure typed onto one home overrides the
    policy — and its argument is good: somebody who types a number into a single home usually
    means "this house is different". It loses because it would undo the reason the policy exists:
    one edit moving forty-two homes. A home on NO policy still quotes from its own ladder, so the
    escape hatch is to take the home off the policy, where the decision is visible on a screen
    rather than buried in one of ninety-five rows.
  */
  const policy = policyForUnit(policies, u.id)
  const fromPolicy = policy ? depositFromPolicy(policy, wholeMonths, { monthlyRent: monthly }) : null
  /*
    AND A HOME ON A POLICY DOES NOT QUIETLY FALL BACK TO ITS LADDER. (Copilot, mtr#115.)

    `fromPolicy ?? ladder` reads as a safe default and is the defect this change exists to remove.
    A policy can answer null — a percentage band on a home with no rent, a stay shorter than the
    first band, a band nobody typed a value into — and the ladder would then decide the deposit on
    a home the screen shows as ON the policy. That is "looks connected and is not" again, which is
    the whole subject of this issue, hidden inside a fallback operator.

    So the policy answers for the homes it holds, including by answering nothing: the deposit then
    falls to the engine's P-07 default with `depositSource` saying where it came from, and a person
    can see that the band did not cover this stay. The ladder is for homes on NO policy.
  */
  const band = policy ? fromPolicy : (cfg ? depositFor(cfg, wholeMonths) : null)
  return {
    unit: {
      id: u.id,
      name: u.name,
      /*
        THE CITY, BECAUSE A DEPOSIT LIMIT IS A PROPERTY OF WHERE THE HOME IS. (mtr#112.)

        Seattle caps the deposit plus non-refundable move-in fees at the first month's rent;
        Washington State caps nothing; Medina, Mercer Island, Bellevue and Kirkland are not
        Seattle. The engine cannot ask that question without this field, and a home with no city
        recorded is reported as unchecked rather than as fine — see `depositLaw.js`.
      */
      city: u.city ?? null,
      monthly,
      minimum,
      /*
        The annual rate Property setup saves. Never carried before (found by review on #50), so every
        stay of twelve months or longer was refused with NO_ANNUAL_RATE even when a person had set one
        — on the quote and on the Listing page alike. A home with no setup row has none, as before.
      */
      annual: cfg?.annualRent ?? null,
      /*
        AND THE GUESTY FIGURE IS THE THIRD FALLBACK, WHICH IS ALSO CLOSED. (Copilot, mtr#115.)

        The ladder was the obvious one. This one sat a line further down: with a policy present and
        no band reached, `band` is null and this fell through to the listing's own deposit — so the
        screen would say "no band for a stay this long, deposit is one month's rent" while the
        quote charged whatever Guesty happens to carry. A home on a policy takes its deposit from
        the policy or from the engine's stated default, and from nothing else.
      */
      deposit: band ? band.cents / 100 : policy ? undefined : u.prices?.securityDepositFee ?? undefined,
      /*
        A CONFIGURED HOME ALWAYS KEEPS ITS CLEANING LINE, EVEN IF GUESTY HAS NO FEE.

        This used to keep only fees with a Guesty amount. A home whose Guesty listing carries no
        cleaning fee therefore lost its cleaning line entirely — and the four prices a person had
        typed into Property setup were never charged, on the quote or anywhere else, because the
        engine only reads `cleaningPrices` for a line that exists. Found writing the Listing page's
        price box, where every cleaning choice came back as nothing.

        The engine charges Property setup's price for the frequency chosen, and refuses a frequency
        priced by neither Property setup nor Guesty (NO_CLEANING_PRICE) rather than borrowing
        another frequency's figure. A home set up with no cleaning price at all is therefore refused,
        not quoted without cleaning: a fee left out is a fee waived (P-05). A home nobody has set up
        keeps the old behaviour — its figures are seeds, and nothing here should read as agreed.
      */
      oneTimeFees: unitFees(u).filter((f) => f.amount != null || (/clean/i.test(f.label) && cfg != null)),
      /*
        The four cleaning prices from Property setup, so the engine charges what a person agreed for
        the schedule actually chosen. Without this, "monthly" and "once at checkout" were priced at
        the same figure per visit — Guesty's single cleaningFee — which nobody in this business set.
      */
      cleaningPrices: cfg?.cleaningPrices ?? null,
      /*
        What this home costs to run, from Property setup. Null means nobody has costed it — which
        the engine reads as "say nothing", never as "free".
      */
      utilities: cfg?.utilities ?? null,
    },
    rentSource: cfg?.monthlyRent != null ? 'set' : 'derived',
    /*
      WHERE THE DEPOSIT CAME FROM, IN ENOUGH DETAIL TO CHECK IT. A figure that appears without its
      reasoning is a figure nobody checks, and "the 6 to 12 months band" does not say WHOSE band
      once there are policies — an agent looking at $2,988 has to be able to get from it to the
      rule that produced it in one step.
    */
    depositSource: fromPolicy
      ? { from: 'policy', policy: policy.name, band: `from ${fromPolicy.band.months} month${fromPolicy.band.months === 1 ? '' : 's'}` }
      : policy
        ? { from: 'policy-silent', policy: policy.name }
        : band
          ? { from: 'ladder', band: band.label }
          : { from: 'listing' },
  }
}
